import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useIntl } from "react-intl-compat";
import { getAreaUnit } from "~/i18n/i18n.utils";

import {
  UpdateSpacePayload,
  useUpdateSpaceMutation,
} from "../../../../components/src/services/spaces.service";
import type { Space } from "../../../../types/spaces.types";

import {
  buildSpaceDetailsFormSchema,
  SpaceDetailsFormValues,
} from "./SpaceDetailsForm/SpaceDetailsForm.schema";

/**
 * Builds the form for the space-details page (title, brand name,
 * description, size) and wires up its save action. Only fields the host
 * actually touched are sent
 */
const useSpaceDetailsForm = (space?: Space) => {
  const intl = useIntl();
  const [updateSpace] = useUpdateSpaceMutation();
  const [errorMsg, setErrorMsg] = React.useState<string>();

  const areaUnit = space ? getAreaUnit(space.country) : "sqft";

  const form = useForm<SpaceDetailsFormValues>({
    resolver: zodResolver(buildSpaceDetailsFormSchema({ intl })),
    values: space
      ? {
          title: space.title ?? "",
          brandName: space.venue_name ?? "",
          description: space.description ?? "",
          size: (areaUnit === "sqft" ? space.sqft : space.sqm) ?? 0,
        }
      : undefined,
  });

  const onSave = async (): Promise<boolean> => {
    setErrorMsg(undefined);
    let success = false;

    await form.handleSubmit(async (values) => {
      if (!space) {
        return;
      }

      const payload: UpdateSpacePayload = {
        title: values.title,
        venue_name: values.brandName,
        description: values.description,
      };
      if (areaUnit === "sqft") {
        payload.sqft = values.size;
      } else {
        payload.sqm = values.size;
      }

      try {
        await updateSpace({ spaceId: space._id, payload }).unwrap();
        success = true;
      } catch {
        setErrorMsg(
          intl.formatMessage({
            id: "hosting.space_details.submit_error",
            defaultMessage: "Something went wrong. Please try again.",
          }),
        );
      }
    })();

    return success;
  };

  return { form, onSave, errorMsg };
};

export { useSpaceDetailsForm };
